/**
 * Unterminated Liquid, caught before the save rather than after it.
 *
 * `checkForInvalidVariables` only compares the names inside `{{ … }}` against the
 * list a surface offers, so it needs a closing `}}` to find anything at all. A
 * half-typed or half-pasted `{{candidate.name` is not an unknown variable, it is
 * broken syntax — it slips past that check, reaches Rails, and comes back as a
 * refused save (CORE-5404).
 *
 * Deliberately narrow: only an opener with no closer, which is what Liquid's own
 * "was not properly terminated" covers and what a human actually produces. A
 * false positive here would block a save that would have worked, so anything
 * subtler is left to the server, which parses properly and is still the gate.
 */

const DELIMITERS = [
  { open: '{{', close: '}}', what: 'variable' },
  { open: '{%', close: '%}', what: 'tag' },
] as const;

/** Enough of the offender to find it by eye, without quoting a paragraph back. */
const EXCERPT_LENGTH = 24;

const excerpt = (from: string) => {
  const line = from.split(/[\r\n]/)[0];
  return line.length > EXCERPT_LENGTH
    ? `${line.slice(0, EXCERPT_LENGTH).trimEnd()}…`
    : line.trimEnd();
};

export interface LiquidSyntaxProblem {
  /** Which field it was found in, named the way the server names it. */
  field: string;
  /** What is wrong, in the reader's terms rather than Liquid's. */
  reason: string;
}

/** The first unterminated opener in one string, or undefined. */
export const findUnterminated = (
  content: string
): { what: string; snippet: string } | undefined => {
  let index = 0;

  while (index < content.length) {
    // Whichever opens first from here — a `{%` inside an unclosed `{{` is the
    // `{{`'s problem, not its own.
    const next = DELIMITERS.map((d) => ({
      d,
      at: content.indexOf(d.open, index),
    }))
      .filter(({ at }) => at !== -1)
      .sort((a, b) => a.at - b.at)[0];

    if (!next) return undefined;

    const closesAt = content.indexOf(
      next.d.close,
      next.at + next.d.open.length
    );
    if (closesAt === -1) {
      return { what: next.d.what, snippet: excerpt(content.slice(next.at)) };
    }

    index = closesAt + next.d.close.length;
  }

  return undefined;
};

/**
 * The first field with unterminated Liquid. Subject before body, and one at a
 * time — the same order and the same restraint the server shows, so the two
 * never disagree about which problem to name first.
 */
export const findLiquidSyntaxProblem = (fields: {
  subject?: string;
  body?: string;
}): LiquidSyntaxProblem | undefined => {
  const ordered: [string, string | undefined][] = [
    ['subject', fields.subject],
    ['body', fields.body],
  ];

  for (const [field, content] of ordered) {
    if (!content) continue;

    const found = findUnterminated(content);
    if (found)
      return {
        field,
        reason: `${found.what} "${found.snippet}" is missing its closing ${
          found.what === 'variable' ? '}}' : '%}'
        }`,
      };
  }

  return undefined;
};

/**
 * An output tag with nothing in it — `{{}}`, `{{ }}`, `{{- -}}`.
 *
 * The same rule Rails applies, deliberately character-for-character: core's
 * `AtsCommTemplate.contains_empty_liquid_variable?` is `/\{\{[-\s]*\}\}/`
 * (CORE-5463). Liquid parses one happily and renders it as empty string, so it is
 * not a syntax error and #findUnterminated cannot see it — it reached the
 * recipient as a hole in the sentence.
 *
 * NOT `checkForInvalidVariables`, which is the other thing entirely: that flags any
 * name absent from the list a surface offers, and that list is not the product's
 * variable surface — validating on it server-side blocked consent-email setup and
 * new-account seeding before it was reverted (CORE-5463). It stays a warning. This
 * is the narrow half the server actually refuses.
 */
const EMPTY_VARIABLE = /\{\{[-\s]*\}\}/;

export const findEmptyVariable = (fields: {
  subject?: string;
  body?: string;
}): LiquidSyntaxProblem | undefined => {
  const ordered: [string, string | undefined][] = [
    ['subject', fields.subject],
    ['body', fields.body],
  ];

  for (const [field, content] of ordered) {
    if (content && EMPTY_VARIABLE.test(content)) {
      return { field, reason: 'a variable has no name in it' };
    }
  }

  return undefined;
};

/**
 * Everything that makes the server refuse this content, in the order the banner
 * names them.
 *
 * Broken syntax first, matching #getInvalidVariableAlertConfig: one stops the save
 * outright, the other is about what the recipient will not see, and naming the
 * lesser problem while the greater one blocks the button is how a sender ends up
 * fixing the wrong thing.
 */
export const findBlockingVariableProblem = (fields: {
  subject?: string;
  body?: string;
}): LiquidSyntaxProblem | undefined =>
  findLiquidSyntaxProblem(fields) ?? findEmptyVariable(fields);
